import type Phaser from "phaser";
import type { CullArea } from "daedalus-renderer-phaser";
import { CLOUD_SHADOW_PRESET, WIND_DASH_PRESET, WIND_WISP_PRESET, cloudShadowsInto, makeCloudSlots, makeWindSlots, windSpritesInto, type CloudField, type Wind, type WindSpritePreset } from "odyssey-shared";
import type { WindSheets } from "./wind-sheets.ts";

/**
 * Wisps, gust dashes and cloud shadows on the page (ADR-144's amendment of 2026-10-31). Where each one is comes from the shared
 * schedule (`windSpritesInto`, `cloudShadowsInto`): a pure function of the clock and the world seed, so nothing is queued, a page
 * that sleeps and wakes draws only what crosses now, and a frame sets the sprites the schedule names and hides the rest. Wisps
 * and dashes are pooled images laid in the world at the view's place; the cloud shadows are stamped opaque onto one render
 * texture the size of the view and laid once at the shadow's opacity and tint, so two clouds that overlap are never darker than
 * one. A frame allocates nothing: the slots, the pools and the stamp's settings are reused.
 */

/**
 * Where the clouds' shadow and the wind draw among the world's objects: above everything that stands and below the dim, and
 * between the dim and the rain, in the order dim, wind, rain.
 */
export const CLOUD_DEPTH = 935_000;
export const WIND_DEPTH = 945_000;

/**
 * How far past the view the shadow reaches on every side, in world pixels, so a moving camera shows no bare edge.
 */
export const SHADOW_PAD = 24;

/**
 * What the view needs of a camera's world view.
 */
export interface WorldRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * What a frame hands the view.
 */
export interface WindFrame {
  view: WorldRect;
  epochMs: number;
  wind: Wind;
  clouds: CloudField;
  reduced: boolean;
  seed: number;
  region: number;
  /** A place where no wisp, dash or shadow is drawn (a roof open over the character), tested at each one's centre; null for none. */
  cover: CullArea | null;
}

/**
 * How many of each the last frame drew.
 */
export interface WindShown {
  wisps: number;
  dashes: number;
  clouds: number;
}

/**
 * A pool of images for one preset, with the texture key and frame each last showed.
 */
interface SpritePool {
  readonly preset: WindSpritePreset;
  readonly images: Phaser.GameObjects.Image[];
  readonly keys: (string | null)[];
  readonly frames: number[];
  readonly slots: ReturnType<typeof makeWindSlots>;
}

/**
 * The page's wind.
 */
export class WindView {
  private readonly scene: Phaser.Scene;
  private readonly sheets: WindSheets;
  private readonly wisps: SpritePool;
  private readonly dashes: SpritePool;
  private readonly cloudSlots = makeCloudSlots();
  private readonly stamp = { alpha: 1, scaleX: 1, scaleY: 1, originX: 0.5, originY: 0.5 };
  private shadow: Phaser.GameObjects.RenderTexture | null = null;
  private shadowWidth = 0;
  private shadowHeight = 0;
  private readonly rect = { x: 0, y: 0, width: 0, height: 0 };
  public readonly shown: WindShown = { wisps: 0, dashes: 0, clouds: 0 };

  /**
   * @param scene The scene the images are added to.
   * @param sheets The wind art the page loaded.
   */
  public constructor(scene: Phaser.Scene, sheets: WindSheets) {
    this.scene = scene;
    this.sheets = sheets;
    this.wisps = makePool(WIND_WISP_PRESET);
    this.dashes = makePool(WIND_DASH_PRESET);
  }

  /**
   * Draws the frame: the wisps and dashes the schedule names, and the cloud shadows.
   *
   * @param frame The clock, the view, the wind and the clouds of the turn.
   */
  public update(frame: WindFrame): void {
    const view = frame.view;
    this.shown.wisps = this.drawSprites(this.wisps, frame);
    this.shown.dashes = this.drawSprites(this.dashes, frame);
    this.shown.clouds = this.drawShadows(frame, view);
  }

  /**
   * Hides everything, for a frame with no wind to draw (Effects Off, reduced motion, indoors).
   */
  public clear(): void {
    this.shown.wisps = this.hideFrom(this.wisps, 0);
    this.shown.dashes = this.hideFrom(this.dashes, 0);
    this.shown.clouds = 0;
    if (this.shadow !== null && this.shadow.visible) this.shadow.setVisible(false);
  }

  /**
   * Destroys the images and the render texture.
   */
  public destroy(): void {
    for (const pool of [this.wisps, this.dashes]) {
      for (const image of pool.images) image.destroy();
      pool.images.length = 0;
    }
    this.shadow?.destroy();
    this.shadow = null;
  }

  /**
   * Sets a pool's images to the sprites the schedule names now.
   *
   * @param pool The pool.
   * @param frame The frame.
   * @returns How many images are showing.
   */
  private drawSprites(pool: SpritePool, frame: WindFrame): number {
    const view = frame.view;
    const count = windSpritesInto(pool.preset, frame.wind, frame.reduced, frame.seed, frame.region, frame.epochMs, view.width, view.height, pool.slots);
    let shown = 0;
    for (let k = 0; k < count; k += 1) {
      const slot = pool.slots[k] as (typeof pool.slots)[number];
      const variant = pool.preset.variants[slot.variant] as (typeof pool.preset.variants)[number];
      const key = this.sheets.keyOf(variant.sheet);
      if (key === null) continue;
      const image = this.imageAt(pool, shown, key);
      if (pool.keys[shown] !== key || pool.frames[shown] !== slot.frame) {
        image.setTexture(key, slot.frame);
        pool.keys[shown] = key;
        pool.frames[shown] = slot.frame;
      }
      const x = Math.round(view.x + slot.x);
      const y = Math.round(view.y + slot.y);
      if (crosses(frame.cover, x, y, image.width, image.height)) continue;
      image.setPosition(x, y).setFlipX(slot.flipX).setAlpha(slot.alpha);
      if (!image.visible) image.setVisible(true);
      shown += 1;
    }
    return this.hideFrom(pool, shown);
  }

  /**
   * The image at an index of a pool, made with the texture it is about to show when the pool has not grown to it yet.
   *
   * @param pool The pool.
   * @param index The index.
   * @param key The texture key of the sprite the image is for.
   * @returns The image.
   */
  private imageAt(pool: SpritePool, index: number, key: string): Phaser.GameObjects.Image {
    const existing = pool.images[index];
    if (existing !== undefined) return existing;
    const image = this.scene.add.image(0, 0, key, 0).setOrigin(0, 0).setDepth(WIND_DEPTH);
    pool.images[index] = image;
    pool.keys[index] = key;
    pool.frames[index] = 0;
    return image;
  }

  /**
   * Hides the images of a pool from an index on.
   *
   * @param pool The pool.
   * @param from The first index to hide.
   * @returns `from`.
   */
  private hideFrom(pool: SpritePool, from: number): number {
    for (let k = from; k < pool.images.length; k += 1) {
      const image = pool.images[k] as Phaser.GameObjects.Image;
      if (image.visible) image.setVisible(false);
    }
    return from;
  }

  /**
   * Stamps the cloud shadows on the render texture and lays it, or hides it when none crosses.
   *
   * @param frame The frame.
   * @param view The camera's world view.
   * @returns How many shadows were stamped.
   */
  private drawShadows(frame: WindFrame, view: WorldRect): number {
    const key = this.sheets.keyOf(CLOUD_SHADOW_PRESET.sheet);
    const rect = this.rect;
    rect.x = view.x - SHADOW_PAD;
    rect.y = view.y - SHADOW_PAD;
    rect.width = view.width + 2 * SHADOW_PAD;
    rect.height = view.height + 2 * SHADOW_PAD;
    const found = key === null ? 0 : cloudShadowsInto(CLOUD_SHADOW_PRESET, frame.clouds, frame.seed, frame.region, frame.epochMs, rect, this.cloudSlots);
    let count = 0;
    for (let k = 0; k < found; k += 1) {
      const slot = this.cloudSlots[k] as (typeof this.cloudSlots)[number];
      if (!crosses(frame.cover, slot.x, slot.y, CLOUD_SHADOW_PRESET.frameWidth * slot.scale, CLOUD_SHADOW_PRESET.frameHeight * slot.scale)) {
        if (count !== k) {
          this.cloudSlots[k] = this.cloudSlots[count] as (typeof this.cloudSlots)[number];
          this.cloudSlots[count] = slot;
        }
        count += 1;
      }
    }
    if (count === 0) {
      if (this.shadow !== null && this.shadow.visible) this.shadow.setVisible(false);
      return 0;
    }
    const texture = this.shadowTexture(Math.ceil(rect.width), Math.ceil(rect.height));
    texture.clear();
    const stamp = this.stamp;
    for (let k = 0; k < count; k += 1) {
      const slot = this.cloudSlots[k] as (typeof this.cloudSlots)[number];
      stamp.alpha = slot.fade;
      stamp.scaleX = slot.flipX ? -slot.scale : slot.scale;
      stamp.scaleY = slot.scale;
      texture.stamp(key as string, slot.shape, slot.x - rect.x + (CLOUD_SHADOW_PRESET.frameWidth * slot.scale) / 2, slot.y - rect.y + (CLOUD_SHADOW_PRESET.frameHeight * slot.scale) / 2, stamp);
    }
    texture.render();
    texture.setPosition(Math.round(rect.x), Math.round(rect.y)).setVisible(true);
    return count;
  }

  /**
   * The render texture shadows are stamped on, made on first use and resized with the view.
   *
   * @param width The width in world pixels.
   * @param height The height in world pixels.
   * @returns The texture.
   */
  private shadowTexture(width: number, height: number): Phaser.GameObjects.RenderTexture {
    if (this.shadow === null) {
      this.shadow = this.scene.add.renderTexture(0, 0, width, height).setOrigin(0, 0).setDepth(CLOUD_DEPTH).setTint(CLOUD_SHADOW_PRESET.tint).setAlpha(CLOUD_SHADOW_PRESET.opacity);
      this.shadowWidth = width;
      this.shadowHeight = height;
    } else if (width !== this.shadowWidth || height !== this.shadowHeight) {
      this.shadow.resize(width, height);
      this.shadowWidth = width;
      this.shadowHeight = height;
    }
    return this.shadow;
  }
}

/**
 * Whether a sprite's cell reaches into a cover: its left end, its centre or its right end, at its mid-height, lies in it. A wisp that
 * crosses a wall is half indoors, and the stroke does not fill its cell, so the ends are tested as well as the centre.
 *
 * @param cover The place where none is drawn, or null.
 * @param x The cell's left in world pixels.
 * @param y The cell's top in world pixels.
 * @param width The cell's width.
 * @param height The cell's height.
 * @returns True when the sprite is to be left out.
 */
function crosses(cover: CullArea | null, x: number, y: number, width: number, height: number): boolean {
  if (cover === null) return false;
  const middle = y + height / 2;
  return cover.contains(x + width / 2, middle) || cover.contains(x, middle) || cover.contains(x + width, middle);
}

/**
 * Makes the pool of a preset.
 *
 * @param preset The preset.
 * @returns The pool, with no image yet.
 */
function makePool(preset: WindSpritePreset): SpritePool {
  return { preset, images: [], keys: [], frames: [], slots: makeWindSlots(Math.max(...preset.count)) };
}
